import * as React from 'react';
import { useMemo, useCallback } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import Slider, { Settings, CustomArrowProps } from 'react-slick';
import MediaQuery from 'react-responsive';

import { AircraftInfoQuery, AircraftInfoParameterType } from '@websky/graphql';
import { useTheme } from '../../../theme';
import { ArrowForward, Chevron, Overweight, Passenger, Planet } from '../../../Icons';
import { MOBILE_MIN_WIDTH } from '../../../utils';
import { SeatIcon } from '../../Icons';
import ImageZoom from '../../../ImageZoom';

const availableParameters = [
	AircraftInfoParameterType.CruisingSpeed,
	AircraftInfoParameterType.MaxFlightRange,
	AircraftInfoParameterType.PassengerCapacity
];

export interface Props {
	className?: string;
	name: string;
	aircraftInfo: AircraftInfoQuery['AircraftInfo'];
	onClose?: () => void;
}

const AircraftPopupContent: React.FC<Props> = ({ className, name, aircraftInfo, onClose }) => {
	const { AircraftPopupContent: css } = useTheme('AircraftPopup');
	const { t } = useTranslation('Aircraft');

	const Arrow: React.FC<CustomArrowProps & { isPrev?: boolean }> = ({ className, style, isPrev, ...props }) => {
		const { currentSlide, slideCount, ...restProps } = props;

		return (
			<div
				className={cn(css.arrow, {
					[css.arrow_prev]: isPrev
				})}
				{...restProps}
			>
				{Chevron}
			</div>
		);
	};

	const sliderSettings: Settings = {
		slidesToShow: 1,
		infinite: true,
		speed: 600,
		dots: true,
		prevArrow: <Arrow isPrev />,
		nextArrow: <Arrow />,
		appendDots: dots => <ul>{dots}</ul>
	};

	const icons = {
		[AircraftInfoParameterType.CruisingSpeed]: Overweight,
		[AircraftInfoParameterType.MaxFlightRange]: Planet,
		[AircraftInfoParameterType.PassengerCapacity]: Passenger
	};

	const filteredParameters = useMemo(() => {
		if (aircraftInfo.parameters?.length) {
			const filteredParams =
				aircraftInfo.parameters?.filter(param => availableParameters.includes(param.type)) ?? [];

			return [...filteredParams].sort(
				(a, b) => availableParameters.indexOf(a.type) - availableParameters.indexOf(b.type)
			);
		}

		return [];
	}, [aircraftInfo.parameters]);

	const renderParameters = useCallback(
		() => (
			<div className={css.groups}>
				{filteredParameters.map((parameter, index) => {
					const isNanValue = isNaN(+parameter.value.replace(/\s+/gm, ''));

					return (
						<div className={css.groups_item} key={index}>
							<div className={css.groups_itemTitle}>
								<div className={css.groups_itemIcon}>{icons[parameter.type]}</div>
								<span>{parameter.title}</span>
							</div>
							<div className={css.groups_itemDescription}>
								<span
									className={cn(css.groups_itemValue, {
										[css.groups_itemValue_text]: isNanValue
									})}
								>
									{parameter.value}
									{!isNanValue && (
										<span className={css.groups_itemUnit}>{t(`Unit_${parameter.type}`)}</span>
									)}
								</span>
							</div>
						</div>
					);
				})}
			</div>
		),
		[filteredParameters]
	);

	const hasImagesOrSchema = aircraftInfo.images?.length > 0 || aircraftInfo.scheme;

	return (
		<div className={cn(css.wrapper, className)}>
			<div className={css.header}>
				<div className={css.back} onClick={onClose}>
					{ArrowForward}
				</div>
				<div className={css.titleWrapper}>
					<div className={css.title}>{aircraftInfo.title ?? name}</div>

					<MediaQuery minWidth={MOBILE_MIN_WIDTH + 1}>{renderParameters()}</MediaQuery>
				</div>
			</div>

			<MediaQuery minWidth={MOBILE_MIN_WIDTH + 1}>
				<Slider {...sliderSettings} className={css.slider} dotsClass={css.slider_dots}>
					{aircraftInfo.images?.map((image, index) => (
						<div className={css.image} key={index}>
							<img src={image} alt="" />
						</div>
					))}

					{aircraftInfo.scheme && (
						<div className={css.image}>
							<img src={aircraftInfo.scheme} alt="" />
						</div>
					)}

					{!hasImagesOrSchema && <div className={cn(css.image, css.default_aircraft)} />}
				</Slider>
			</MediaQuery>

			<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
				<div className={css.content}>
					<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>{renderParameters()}</MediaQuery>

					{aircraftInfo.images?.map((image, index) => (
						<div key={index} className={css.image}>
							<img src={image} alt="" />
						</div>
					))}

					{aircraftInfo.scheme && (
						<div className={css.schema}>
							<ImageZoom
								className={css.schemaZoom}
								src={aircraftInfo.scheme}
								alt=""
								direction="rotate-90"
								zoom={3}
							/>
						</div>
					)}

					{!hasImagesOrSchema && <div className={cn(css.image, css.default_aircraft)} />}
				</div>

				{aircraftInfo.scheme && (
					<div className={css.footer}>
						<div className={css.cabins}>
							{aircraftInfo.cabins?.map(cabin => (
								<div className={css.cabins_item} key={cabin.title}>
									<div
										className={cn(css.cabins_itemIcon, {
											[css[cabin.title]]: css?.[cabin.title]
										})}
									>
										{SeatIcon}
									</div>
									<div className={css.cabins_itemTitle}>{t(`${cabin.title}`)}</div>
								</div>
							))}
						</div>
					</div>
				)}
			</MediaQuery>
		</div>
	);
};

export default AircraftPopupContent;
